import { BarChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import ReactEChartsCore from "echarts-for-react/lib/core";

import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { ABBREVIATED_OPTION, NO_SYMBOL, useConfig } from "@/hooks/useConfig";
import { colors, toCompactNumber } from "@/Utils";
import { getHoverTooltip } from "@/Utils/ECharts/HoverTooltip";
import { extraCssText } from "@/Utils/extraCssText";

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent,
]);

const getSeriesTotals = (seriesData = []) => {
  if (seriesData.length === 0) {
    return [];
  }

  return seriesData.reduce((acc, current) => {
    return acc.map((item, index) => {
      return item + (current.data[index] ?? 0);
    });
  }, new Array(seriesData[0].data.length).fill(0));
};

const BasicBarStackGraph = ({
  hoverData = {
    componentName: "",
    data: {},
  },
  xAxisData,
  seriesData,
  heightPx = "360px",
  tooltipTrigger = "axis",
  legend = undefined,
  bottomOffset = "5%",
  rotateLegendDg = 0,
  currencySymbol = "$",
  isDollarData,
}) => {
  const { theme } = useInfo();
  const color = theme.mode === "dark" ? colors.white : colors.black;

  const {
    config,
    config: { showReturnInsteadOfRevenue },
  } = useConfig();
  const formatCurrency = config.currency.formatCurrency;

  const yAxisFormatOptions = showReturnInsteadOfRevenue
    ? { ...NO_SYMBOL, ...ABBREVIATED_OPTION }
    : ABBREVIATED_OPTION;

  const options = {
    grid: {
      left: "1%",
      right: "0%",
      top: "4%",
      bottom: bottomOffset,
      containLabel: true,
    },
    textStyle: {
      color: color,
      fontFamily: "Montserrat",
    },
    tooltip: {
      borderColor: "transparent",
      textStyle: {
        color: colors.white,
      },
      formatter: (params) => {
        return getHoverTooltip({
          params,
          data: {
            ...hoverData.data,
            // useful for when trigger is an "item" and axis data is not propagated to tooltip by default
            seriesTotals: getSeriesTotals(seriesData),
            currencySymbol: currencySymbol,
            config: config,
          },
          componentName: hoverData.componentName,
          formatCurrency,
        });
      },
      backgroundColor: colors.cardBg,
      extraCssText: extraCssText,
      trigger: tooltipTrigger,
      axisPointer: {
        type: "shadow",
      },
    },
    xAxis: [
      {
        axisLabel: {
          fontSize: 10,
          rotate: rotateLegendDg,
        },
        data: xAxisData,
        silent: false,
        splitLine: {
          show: false,
        },
        splitArea: {
          show: false,
        },
        offset: 5,
        axisTick: {
          alignWithLabel: true,
        },
      },
    ],
    yAxis: {
      nameTextStyle: {
        fontSize: 10,
      },
      type: "value",

      axisLabel: {
        fontSize: 10,
        formatter: (value) => {
          if (isDollarData) {
            return formatCurrency(value, yAxisFormatOptions);
          }
          return toCompactNumber(value);
        },
      },
      splitLine: {
        lineStyle: {
          color: colors.lightGray,
        },
      },
    },
    series: seriesData,
    legend: legend,
  };

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={options}
      notMerge={true}
      lazyUpdate={true}
      style={{
        height: heightPx,
      }}
    />
  );
};

export default BasicBarStackGraph;
